import React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  PopoverTrigger,
  PopoverContent,
  Popover,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";

import { ChatModelSelector } from "./ChatModelSelector";
import { TemperatureSelector } from "./TemperatureSelector";
import { ArtefactsPanelToggle } from "./ArtefactsPanelToggle";

interface InvestAIHeaderControlsProps {
  /** The current workflow */
  workflow: { id: string; name: string };
  /** Whether this is a specialized workflow (disables temperature controls) */
  isSpecialized: boolean;
  /** Whether there is content (artefacts/citations) to show toggle for */
  hasContent: boolean;
  /** Whether the artefacts panel is currently visible */
  artefactsVisible: boolean;
  /** Callback to toggle the artefacts panel */
  onToggleArtefacts: () => void;
}

/**
 * Header controls specific to the Invest AI brand.
 * Includes model selector, temperature settings, and content panel toggle.
 */
export const InvestAIHeaderControls: React.FC<InvestAIHeaderControlsProps> = ({
  isSpecialized,
  hasContent,
  artefactsVisible,
  onToggleArtefacts,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap justify-between px-4 items-center gap-2">
      <div className="flex items-center">
        <ChatModelSelector position="bottom-start" />
        {!isSpecialized ? (
          <Popover>
            <PopoverTrigger>
              <Button
                isIconOnly
                aria-label={t("Open temperature popover")}
                className="bg-transparent hover:bg-transparent focus-visible:ring-2 focus-visible:ring-brand-primary active:bg-transparent"
              >
                <Icon className="text-brand-primary" icon="solar:settings-bold" width={18} />
              </Button>
            </PopoverTrigger>

            <PopoverContent
              className="bg-content1 p-4 rounded-xl shadow-xl w-[min(280px,90vw)]"
              placement="bottom start"
            >
              <TemperatureSelector />
            </PopoverContent>
          </Popover>
        ) : (
          <Tooltip>
            <TooltipTrigger>
              <Button
                disabled
                isIconOnly
                aria-label={t("Open temperature popover")}
                className="bg-transparent hover:bg-transparent focus-visible:ring-2 focus-visible:ring-brand-primary active:bg-transparent disabled:bg-transparent disabled:cursor-not-allowed"
              >
                <Icon className="text-brand-primary" icon="solar:settings-bold" width={18} />
              </Button>
            </TooltipTrigger>
            <TooltipContent showArrow className="max-w-[280px]" placement="bottom-start">
              {t("disabled-temperature-workflow")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Content panel toggle button */}
      {hasContent && (
        <ArtefactsPanelToggle isVisible={artefactsVisible} onToggle={onToggleArtefacts} />
      )}
    </div>
  );
};
